import { useEffect, useState } from 'preact/hooks';
import * as st from '../state/store';
import { TOURS, TOUR_BY_ID, loadStops, startTour, gotoStop, exitTour, visibleStops, type TourStop } from '../tours/tours';
import { canAccess, tier } from '../entitlements/tier';
import { BRAND } from '../brand';
import { CopyViewState } from './CopyViewState';

const TIER_LABEL = { free: '', supporter: 'Supporter tour', school: 'School tour' } as const;

/** Tour index (no tour running) or the tour player (tour running). */
export function TourPanel() {
  const t = st.tour.value;
  return t ? <TourPlayer id={t.id} stop={t.stop} /> : <TourIndex />;
}

function TourIndex() {
  const groups: Array<['free' | 'supporter' | 'school', string]> = [['free', 'Tours'], ['supporter', 'Supporter tours'], ['school', 'School tours']];
  return (
    <div class="tours" data-testid="tour-panel">
      <h1 class="card-title">Guided tours</h1>
      <p class="muted">Short stories through the body. Use ← and → to move between stops.</p>
      {groups.map(([tier, label]) => (
        <section key={tier}>
          <h2>{label}</h2>
          <ul class="tour-list">
            {TOURS.filter((x) => x.tier === tier).map((x) => (
              <li key={x.id}>
                <button type="button" class="tour-item" onClick={() => startTour(x.id)} data-testid="tour-item" data-id={x.id}>
                  <span class="tour-title">
                    {x.title}
                    {!canAccess(x.tier) && (
                      <span class="lock" data-testid="tour-lock">
                        <span aria-hidden="true"> 🔒</span>
                        <span class="visually-hidden"> (locked: first stop free)</span>
                      </span>
                    )}
                  </span>
                  <span class="tour-meta">
                    {x.minutes} min · {x.stops.length} stops{x.yearLevels ? ` · ${x.yearLevels}` : ''}
                  </span>
                  <span class="tour-summary">{x.summary}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function TourPlayer({ id, stop }: { id: string; stop: number }) {
  const t = TOUR_BY_ID.get(id);
  const [stops, setStops] = useState<TourStop[] | null>(null);
  useEffect(() => {
    let live = true;
    loadStops(id).then((s) => live && setStops(s));
    return () => {
      live = false;
    };
  }, [id]);
  if (!t) return null;
  const s = stops?.[stop];
  const unlocked = visibleStops(t);
  const lockedNext = stop + 1 >= unlocked && stop + 1 < t.stops.length;
  return (
    <div class="tour-player" data-testid="tour-player" data-tour={id} data-stop={stop}>
      <p class="eyebrow">
        {TIER_LABEL[t.tier] && <span class="badge">{TIER_LABEL[t.tier]}</span>}
        <span data-testid="tour-progress">
          Stop {stop + 1} of {t.stops.length}
        </span>
      </p>
      <h1 class="card-title">{t.title}</h1>
      <h2 class="stop-title" data-testid="stop-title">{s?.title ?? t.stops[stop]?.title}</h2>
      {s ? <p class="stop-text card" data-testid="stop-text">{s.text}</p> : <p class="muted">Loading…</p>}
      {lockedNext && (
        <div class="placeholder" data-testid="tour-locked">
          <p>
            The rest of this {t.tier === 'school' ? 'school' : 'supporter'} tour is for {t.tier === 'school' ? 'schools with a licence' : 'supporters'}.{' '}
            <a href={t.tier === 'school' ? '/teachers/' : '/supporter/'}>Find out more</a> or <a href="#unlock" onClick={(e) => { e.preventDefault(); st.panel.value = 'unlock'; }} data-testid="tour-unlock">enter a licence key</a>.
          </p>
        </div>
      )}
      {t.tier === 'free' && stop === t.stops.length - 1 && tier.value === 'none' && (
        <p class="tour-end muted" data-testid="tour-end-support">
          Enjoyed this? Supporters get four more tours and keep {BRAND.name} free for everyone.{' '}
          <a href="#unlock" onClick={(e) => { e.preventDefault(); st.panel.value = 'unlock'; }}>Support {BRAND.name}</a>
        </p>
      )}
      {t.sponsor && (
        <p class="small muted">
          Supported by <a href={t.sponsor.url} rel="noopener sponsored">{t.sponsor.name}</a>
        </p>
      )}
      <div class="card-actions" role="toolbar" aria-label="Tour controls">
        <button type="button" class="btn small" onClick={() => gotoStop(stop - 1)} disabled={stop === 0} data-testid="tour-prev">← Back</button>
        <button type="button" class="btn small primary" onClick={() => gotoStop(stop + 1)} disabled={stop + 1 >= unlocked} data-testid="tour-next">Next →</button>
        <button type="button" class="btn small ghost" onClick={exitTour} data-testid="tour-exit">Exit tour</button>
      </div>
      <CopyViewState />
    </div>
  );
}
